import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Plus, Settings } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi, useClientOptions } from "@/hooks/useApi";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, FilterBar, DataTable, StatusBadge, ErrorBox } from "@/components/common/ui-bits";
import { FormDialog } from "@/components/common/FormDialog";
import { fmtDate, money, optionList, INVOICE_STATUSES, INVOICE_TYPES } from "@/lib/format";

const Sum = ({ label, value, testid }) => (
  <div className="card-box p-4"><p className="text-xs text-slate-500">{label}</p><p className="num mt-1 text-xl font-semibold text-slate-900" data-testid={testid}>{value}</p></div>
);

function SettingsDialog({ open, onOpenChange }) {
  const { data } = useApi(open ? "/invoice-settings" : null);
  if (!data) return null;
  const fields = [
    { name: "name", label: "Nombre del negocio", required: true }, { name: "tax_id", label: "Identificación fiscal" },
    { name: "address", label: "Dirección", full: true }, { name: "phone", label: "Teléfono" }, { name: "email", label: "Correo" },
    { name: "website", label: "Sitio web" }, { name: "default_conditions", label: "Condiciones por defecto", type: "textarea" },
    ...Object.entries(data.series).flatMap(([k, s]) => [
      { name: `${k}__prefix`, label: `Serie ${s.label}: prefijo`, required: true, help: "Mayúsculas y números (ej. INV)" },
      { name: `${k}__start`, label: `Serie ${s.label}: número inicial`, type: "number", min: 1, required: true },
      { name: `${k}__digits`, label: `Serie ${s.label}: dígitos`, type: "number", min: 1, required: true },
      { name: `${k}__include_year`, label: `Serie ${s.label}: incluir año`, type: "checkbox" }]),
  ];
  const initial = { ...data.business, default_conditions: data.default_conditions,
    ...Object.fromEntries(Object.entries(data.series).flatMap(([k, s]) => Object.entries(s).map(([f, v]) => [`${k}__${f}`, v]))) };
  return (
    <FormDialog open={open} onOpenChange={onOpenChange} title="Configuración de facturación" testid="invoice-settings-form" wide fields={fields} initial={initial}
      description="Datos del negocio que aparecen en el PDF y numeración automática por serie."
      onSubmit={async (v) => {
        const series = Object.fromEntries(Object.entries(data.series).map(([k, s]) => [k, { label: s.label, prefix: (v[`${k}__prefix`] || "").toUpperCase(),
          start: v[`${k}__start`], digits: v[`${k}__digits`], include_year: v[`${k}__include_year`] }]));
        const { name, tax_id, address, phone, email, website, default_conditions } = v;
        await api.put("/invoice-settings", { business: { name, tax_id, address, phone, email, website }, default_conditions, series });
        toast.success("Configuración guardada");
      }} />
  );
}

export default function Invoices() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const portal = user.role === "client";
  const [f, setF] = useState({});
  const { data, loading, error } = useApi("/invoices", f);
  const clients = useClientOptions();
  const [settings, setSettings] = useState(false);
  const navigate = useNavigate();
  const base = portal ? "/portal/facturas" : "/facturas";
  const live = (data || []).filter((i) => !["Borrador", "Cancelada", "Anulada", "Reembolsada"].includes(i.status));
  const sum = (k) => money(live.reduce((s, i) => s + (i[k] || 0), 0));

  return (
    <div>
      <PageHeader title={portal ? "Mis facturas" : "Facturas"} subtitle="Las facturas emitidas no se eliminan: se anulan y se crea una factura corregida."
        actions={isAdmin && <>
          <Button variant="outline" onClick={() => setSettings(true)} data-testid="invoice-settings-button"><Settings className="mr-2 h-4 w-4" />Configuración</Button>
          <Button onClick={() => navigate("/facturas/nueva")} data-testid="new-invoice-button"><Plus className="mr-2 h-4 w-4" />Nueva factura</Button>
        </>} />
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Sum label="Total facturado" value={sum("total")} testid="invoices-total-billed" />
        <Sum label="Total pagado" value={sum("paid")} testid="invoices-total-paid" />
        <Sum label="Saldo pendiente" value={sum("balance")} testid="invoices-total-balance" />
      </div>
      <FilterBar values={f} onChange={setF} testid="invoices-filters" filters={[
        { key: "status", label: "Estado", options: optionList(INVOICE_STATUSES) }, { key: "type", label: "Tipo", options: optionList(INVOICE_TYPES) },
        ...(portal ? [] : [{ key: "client_id", label: "Cliente", options: clients }])]} />
      <ErrorBox text={error} />
      <DataTable testid="invoices-table" quickView={(r) => r.number || "Borrador"} loading={loading} rows={data} onRowClick={(r) => navigate(`${base}/${r.id}`)} empty="No hay facturas."
        columns={[
          { key: "number", label: "Número", render: (r) => <span className="num text-xs">{r.number || "BORRADOR"}</span> },
          ...(portal ? [] : [{ key: "client_name", label: "Cliente" }]),
          { key: "type", label: "Tipo" },
          { key: "due_date", label: "Vence", render: (r) => fmtDate(r.due_date) },
          { key: "total", label: "Total", render: (r) => <span className="num">{money(r.total)}</span> },
          { key: "balance", label: "Saldo", render: (r) => <span className="num">{money(r.balance)}</span> },
          { key: "status", label: "Estado", render: (r) => <StatusBadge status={r.status} /> },
        ]} />
      {isAdmin && <SettingsDialog open={settings} onOpenChange={(o) => { setSettings(o); if (!o) { /* noop */ } }} />}
    </div>
  );
}

export const voidFields = [{ name: "reason", label: "Motivo de la anulación", type: "textarea", required: true, minLength: 3 },
  { name: "create_corrected", label: "Crear factura corregida (borrador con los mismos datos)", type: "checkbox", default: true }];
export { errMsg };
